import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { DollarSign, Users, Gauge, Activity, Plus, Wifi, X, Trash2, Signal, ShieldAlert, BadgeCheck } from "lucide-react";
import { PayoutsPanel } from "../components/PayoutsPanel";
import { GuestReviews } from "../components/GuestReviews";

export default function HostDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [spots, setSpots] = useState([]);
  const [guests, setGuests] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const [s, h, g] = await Promise.all([api.get("/host/stats"), api.get("/hotspots/mine"), api.get("/host/guests")]);
      setStats(s.data); setSpots(h.data); setGuests(g.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const disconnect = async (id) => {
    try { await api.post(`/host/guests/${id}/disconnect`); toast.success("Guest disconnected"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const removeSpot = async (id) => {
    try { await api.delete(`/hotspots/${id}`); toast.success("Hotspot removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const metrics = [
    { icon: DollarSign, label: "Monthly earnings", value: stats ? `$${stats.total_earnings}` : "—" },
    { icon: Users, label: "Active guests", value: stats?.active_guests ?? "—" },
    { icon: Gauge, label: "Avg speed", value: stats ? `${stats.avg_speed} Mbps` : "—" },
    { icon: Activity, label: "Uptime", value: stats ? `${stats.uptime}%` : "—" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Host Dashboard</h1>
            <p className="text-muted-foreground">Welcome back, {user?.name?.split(" ")[0] || "Host"}.</p>
          </div>
          <button onClick={() => setShowForm(true)} data-testid="add-hotspot-btn" className="h-11 px-5 rounded-full bg-foreground text-background text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity self-start"><Plus size={16} /> List a hotspot</button>
        </div>

        {user?.verification_status !== "verified" && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-center gap-3" data-testid="host-verify-notice">
            <ShieldAlert size={18} className="text-amber-500" />
            <p className="text-sm flex-1">Verify your identity to earn the trusted host badge and go live faster.</p>
            <button onClick={() => navigate("/verify")} className="text-sm px-4 py-2 rounded-full border border-border hover:bg-accent">Verify now</button>
          </div>
        )}

        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-10">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-2xl border border-border bg-card p-5" data-testid={`metric-${m.label.toLowerCase().replace(/\s/g,'-')}`}>
              <m.icon size={18} className="text-muted-foreground mb-3" />
              <div className="font-display text-2xl font-bold tracking-tight">{m.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.label}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="font-display text-xl font-medium mb-4">Your hotspots</h2>
            {spots.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <Wifi size={26} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground text-sm">No hotspots yet. List your router to start earning.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-hotspots-list">
                {spots.map((s) => (
                  <div key={s.hotspot_id} className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4">
                    <div className="h-11 w-11 rounded-xl bg-accent grid place-items-center shrink-0"><Signal size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><p className="font-medium truncate">{s.name}</p>{s.wifi6 && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-accent">Wi-Fi6</span>}</div>
                      <p className="text-xs text-muted-foreground font-mono">{s.ssid} · {s.speed_mbps} Mbps · ${s.monthly_price}/mo</p>
                    </div>
                    <button onClick={() => removeSpot(s.hotspot_id)} data-testid={`delete-hotspot-${s.hotspot_id}`} className="h-9 w-9 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-500/40 transition-colors"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <h2 className="font-display text-xl font-medium mb-4">Connected guests</h2>
            {guests.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center"><Users size={24} className="mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground text-sm">No guests connected yet.</p></div>
            ) : (
              <div className="space-y-3" data-testid="guests-list">
                {guests.map((g) => (
                  <div key={g.booking_id} className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-foreground text-background grid place-items-center text-xs font-bold">{(g.guest_name || "G")[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium flex items-center gap-1 truncate">{g.guest_name || "Guest"} {g.guest_verified && <BadgeCheck size={13} className="text-emerald-500 shrink-0" />}</p>
                      <p className="text-xs text-muted-foreground font-mono">{g.signal} · {g.usage_gb} GB · {g.status}</p>
                    </div>
                    {g.status === "connected" && <button onClick={() => disconnect(g.booking_id)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-accent" data-testid={`disconnect-${g.booking_id}`}>Disconnect</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-2">
          <PayoutsPanel />
          <GuestReviews />
        </div>
      </section>

      {showForm && <HotspotForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function HotspotForm({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", router_model: "", city: "", address: "", speed_mbps: 300, daily_price: 4.99, monthly_price: 19.99, max_guests: 5, bandwidth_limit_gb: 200, wifi6: true, description: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/hotspots", { ...f, speed_mbps: Number(f.speed_mbps), daily_price: Number(f.daily_price), monthly_price: Number(f.monthly_price), max_guests: Number(f.max_guests), bandwidth_limit_gb: Number(f.bandwidth_limit_gb) });
      toast.success("Hotspot listed!");
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setSaving(false); }
  };

  const Input = ({ k, label, type = "text", ...rest }) => (
    <label className="block"><span className="text-xs text-muted-foreground">{label}</span>
      <input type={type} value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`hotspot-${k}`}
        className="mt-1 w-full h-10 px-3 rounded-lg bg-background border border-input text-sm outline-none focus:border-ring" {...rest} /></label>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      <form onSubmit={submit} className="relative w-full max-w-lg my-8 rounded-2xl border border-border bg-card p-8 rise" data-testid="hotspot-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X size={20} /></button>
        <h2 className="font-display text-xl font-bold mb-5 flex items-center gap-2"><Wifi size={18} /> List a hotspot</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input k="name" label="Hotspot name" required /></div>
          <Input k="router_model" label="Router model" />
          <Input k="speed_mbps" label="Speed (Mbps)" type="number" />
          <Input k="city" label="City" required />
          <Input k="address" label="Area / address" />
          <Input k="daily_price" label="Daily price ($)" type="number" step="0.01" />
          <Input k="monthly_price" label="Monthly price ($)" type="number" step="0.01" />
          <Input k="max_guests" label="Max guests" type="number" />
          <Input k="bandwidth_limit_gb" label="Bandwidth cap (GB)" type="number" />
          <label className="col-span-2 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={f.wifi6} onChange={(e) => upd("wifi6", e.target.checked)} data-testid="hotspot-wifi6" className="h-4 w-4 accent-current" />
            <span className="text-sm">Wi-Fi 6 capable router</span>
          </label>
          <label className="col-span-2 block"><span className="text-xs text-muted-foreground">Description</span>
            <textarea value={f.description} onChange={(e) => upd("description", e.target.value)} rows={3} data-testid="hotspot-description" className="mt-1 w-full px-3 py-2 rounded-lg bg-background border border-input text-sm outline-none focus:border-ring resize-none" /></label>
        </div>
        <button type="submit" disabled={saving} data-testid="hotspot-save" className="mt-5 w-full h-11 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">{saving ? "Saving…" : "Publish hotspot"}</button>
      </form>
    </div>
  );
}
